/**
 * QQPlot — observed quantile vs theoretical normal quantile per well.
 * Native hand-rolled SVG (no Recharts).
 *
 * Each well becomes a scatter series; a dashed identity line shows where
 * points would fall under a perfect normal distribution. Rectangle zoom +
 * dblclick reset via useChartView.
 */

import { useState, useMemo, useRef, useEffect, useCallback, useId } from 'react';
import { scaleLinear } from 'd3-scale';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
import { useRegulatoryLines } from '../../../hooks/useRegulatoryLines';
import { WELL_COLORS } from '../../chartTheme';
import { ChartTooltip } from '../../ChartTooltip';
import { useChartView } from '../../interactions/useChartView';
import { ChartViewResetChip } from '../../ChartViewResetChip';

interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

function convertConc(val: number | null, _unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

/** Inverse normal approximation (Beasley-Springer-Moro). */
function qnorm(p: number): number {
  if (p <= 0) return -Infinity;
  if (p >= 1) return Infinity;
  if (p === 0.5) return 0;
  const a = [-3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.383577518672690e2, -3.066479806614716e1, 2.506628277459239];
  const b = [-5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1, -1.328068155288572e1];
  const c = [-7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783];
  const d = [7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416];
  const pLow = 0.02425;
  const pHigh = 1 - pLow;
  let q: number, r: number;
  if (p < pLow) {
    q = Math.sqrt(-2 * Math.log(p));
    return (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  } else if (p <= pHigh) {
    q = p - 0.5;
    r = q * q;
    return (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q /
      (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
  } else {
    q = Math.sqrt(-2 * Math.log(1 - p));
    return -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  }
}

interface Pt {
  theoretical: number;
  observed: number;
  well: string;
  color: string;
}

const MARGIN = { top: 12, right: 24, bottom: 40, left: 64 };
const CHART_HEIGHT = 420;

export function QQPlot({ data, unit }: Props) {
  const allAnalytes = useMemo(() => {
    const map = new Map<string, string>();
    for (const well of data.wells) {
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!map.has(code)) map.set(code, analyte.compound);
      }
    }
    return Array.from(map.entries()).map(([code, compound]) => ({ code, compound }));
  }, [data.wells]);

  const defaultAnalyte = useMemo(() => {
    const benzene = allAnalytes.find((a) => a.compound.toLowerCase() === 'benzene');
    return benzene?.code ?? allAnalytes[0]?.code ?? '';
  }, [allAnalytes]);

  const [selectedAnalyte, setSelectedAnalyte] = useState<string>(defaultAnalyte);
  const regVisibleCodes = useMemo(() => new Set([selectedAnalyte]), [selectedAnalyte]);
  const { lines: regulatoryLines } = useRegulatoryLines(data, regVisibleCodes);

  const { points, refLine } = useMemo(() => {
    const pts: Pt[] = [];
    data.wells.forEach((well, wi) => {
      const a = well.analytes[selectedAnalyte];
      if (!a) return;
      const values = a.measurements
        .map((m) => convertConc(m.concentration, unit))
        .filter((v): v is number => v !== null)
        .sort((x, y) => x - y);
      if (values.length < 2) return;
      const n = values.length;
      const color = WELL_COLORS[wi % WELL_COLORS.length];
      values.forEach((val, i) => {
        const p = (i + 0.5) / n;
        const theoretical = qnorm(p);
        if (!isFinite(theoretical)) return;
        pts.push({ theoretical, observed: val, well: well.well_name, color });
      });
    });
    const theos = pts.map((p) => p.theoretical);
    const tMin = theos.length ? Math.min(...theos) : -3;
    const tMax = theos.length ? Math.max(...theos) : 3;
    return { points: pts, refLine: [{ x: tMin, y: tMin }, { x: tMax, y: tMax }] };
  }, [data.wells, selectedAnalyte, unit]);

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(800);
  const clipId = `qq-clip-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, CHART_HEIGHT - MARGIN.top - MARGIN.bottom);

  const baseXDomain = useMemo<readonly [number, number]>(() => {
    if (points.length === 0) return [-3, 3];
    let lo = Infinity, hi = -Infinity;
    for (const p of points) {
      if (p.theoretical < lo) lo = p.theoretical;
      if (p.theoretical > hi) hi = p.theoretical;
    }
    const pad = (hi - lo) * 0.05;
    return [lo - pad, hi + pad];
  }, [points]);

  const baseYDomain = useMemo<readonly [number, number]>(() => {
    if (points.length === 0) return [0, 1];
    let lo = Infinity, hi = -Infinity;
    for (const p of points) {
      if (p.observed < lo) lo = p.observed;
      if (p.observed > hi) hi = p.observed;
    }
    for (const line of regulatoryLines) {
      if (line.value > hi) hi = line.value;
    }
    const pad = (hi - lo) * 0.05;
    return [Math.min(lo - pad, 0), hi + pad];
  }, [points, regulatoryLines]);

  const xPxToDataRef = useRef<(px: number) => number>(() => 0);
  const yPxToDataRef = useRef<(py: number) => number>(() => 0);
  const view = useChartView(
    svgRef,
    (px) => xPxToDataRef.current(px),
    (py) => yPxToDataRef.current(py),
    {
      baseXDomain,
      baseYDomain,
      // Both axes carry meaning — free 2D box-zoom.
      dragZoomAxes: 'xy',
      chartArea: {
        top: MARGIN.top,
        bottom: MARGIN.top + innerHeight,
        left: MARGIN.left,
        right: MARGIN.left + innerWidth,
      },
    },
  );

  const xScale = useMemo(
    () => scaleLinear().domain([view.xDomain[0], view.xDomain[1]]).range([MARGIN.left, MARGIN.left + innerWidth]),
    [view.xDomain, innerWidth],
  );
  const yScale = useMemo(
    () => scaleLinear().domain([view.yDomain[0], view.yDomain[1]]).range([MARGIN.top + innerHeight, MARGIN.top]).nice(),
    [view.yDomain, innerHeight],
  );
  xPxToDataRef.current = (px: number) => xScale.invert(px);
  yPxToDataRef.current = (py: number) => yScale.invert(py);

  const xTicks = useMemo(() => xScale.ticks(7), [xScale]);
  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  const formatX = (v: number) => v.toFixed(1);
  const formatY = (v: number) => {
    if (v === 0) return '0';
    const abs = Math.abs(v);
    if (abs < 0.01) return v.toExponential(1);
    if (abs < 1) return v.toPrecision(2);
    return v.toFixed(1);
  };

  interface HoverState { point: Pt; clientX: number; clientY: number }
  const [hover, setHover] = useState<HoverState | null>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const svg = svgRef.current; if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const lx = e.clientX - rect.left;
      const ly = e.clientY - rect.top;
      let best: Pt | null = null;
      let bestDsq = 14 * 14;
      for (const p of points) {
        const px = xScale(p.theoretical);
        const py = yScale(p.observed);
        const dx = px - lx;
        const dy = py - ly;
        const dsq = dx * dx + dy * dy;
        if (dsq < bestDsq) {
          bestDsq = dsq;
          best = p;
        }
      }
      if (best) setHover({ point: best, clientX: e.clientX, clientY: e.clientY });
      else setHover(null);
    },
    [points, xScale, yScale],
  );
  const handleMouseLeave = useCallback(() => setHover(null), []);

  if (!data.wells.length) {
    return (
      <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
        NO ANALYTICAL DATA AVAILABLE
      </div>
    );
  }

  return (
    <div role="img" aria-label="QQ plot comparing observed vs theoretical distribution">
      <div className="flex items-center gap-3 mb-4">
        <label className="text-xs font-medium" style={{ color: 'var(--dv-text-muted)' }}>
          Analyte
        </label>
        <select
          value={selectedAnalyte}
          onChange={(e) => setSelectedAnalyte(e.target.value)}
          className="text-xs rounded px-2 py-1 border"
          style={{
            background: 'var(--dv-bg-card)',
            borderColor: 'var(--dv-border-subtle)',
            color: 'var(--dv-text-primary)',
          }}
        >
          {allAnalytes.map((a) => (
            <option key={a.code} value={a.code}>{a.compound}</option>
          ))}
        </select>
      </div>

      <p className="text-xs mb-3" style={{ color: 'var(--dv-text-muted)' }}>
        Q-Q (quantile-quantile) plot comparing your data distribution against a theoretical normal
        distribution. Points falling on the dashed identity line indicate normally distributed data.
      </p>

      <ChartViewResetChip isZoomed={view.isZoomed} onReset={view.reset} />

      <div ref={containerRef}>
        <svg
          ref={svgRef}
          width={width || '100%'}
          height={CHART_HEIGHT}
          style={{ display: 'block', userSelect: 'none', touchAction: 'none', cursor: 'crosshair' }}
        >
          <defs>
            <clipPath id={clipId}>
              <rect x={MARGIN.left} y={MARGIN.top} width={innerWidth} height={innerHeight} />
            </clipPath>
          </defs>

          <g clipPath={`url(#${clipId})`}>
            {/* Gridlines */}
            {yTicks.map((t) => {
              const y = yScale(t);
              return (
                <line
                  key={`grid-${t}`}
                  x1={MARGIN.left}
                  x2={MARGIN.left + innerWidth}
                  y1={y}
                  y2={y}
                  stroke="var(--dv-chart-grid)"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
              );
            })}

            {/* Identity reference line */}
            <line
              x1={xScale(refLine[0].x)}
              y1={yScale(refLine[0].y)}
              x2={xScale(refLine[1].x)}
              y2={yScale(refLine[1].y)}
              stroke="var(--dv-text-muted)"
              strokeWidth={1.5}
              strokeDasharray="6 4"
            />

            {/* Regulatory horizontal lines */}
            {regulatoryLines.map((line) => {
              const v = convertConc(line.value, unit);
              if (v === null) return null;
              const [yLo, yHi] = view.yDomain;
              if (v > yHi || v < yLo) return null;
              const y = yScale(v);
              const dashArray = line.style === 'dashed' ? '8 4' : line.style === 'dotted' ? '2 4' : undefined;
              return (
                <g key={line.key} style={{ filter: 'url(#dv-mcl-glow)' }}>
                  <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={y} y2={y} stroke={line.color} strokeWidth={1} strokeDasharray={dashArray} />
                  <text x={MARGIN.left + innerWidth - 4} y={y - 4} textAnchor="end" fontSize={9} fill={line.color}>
                    {`${line.label} ${line.analyteName}`}
                  </text>
                </g>
              );
            })}

            {/* Scatter dots */}
            {points.map((p, i) => {
              const isHovered = hover?.point === p;
              return (
                <circle
                  key={`pt-${i}`}
                  cx={xScale(p.theoretical)}
                  cy={yScale(p.observed)}
                  r={isHovered ? 5 : 3.5}
                  fill={p.color}
                  stroke="var(--dv-bg-card)"
                  strokeWidth={isHovered ? 2 : 1}
                  opacity={0.85}
                  style={isHovered ? { filter: 'url(#dv-hover-halo)' } : undefined}
                />
              );
            })}
          </g>

          {/* Y-axis */}
          <line x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
          {yTicks.map((t) => {
            const y = yScale(t);
            return (
              <g key={`yt-${t}`}>
                <line x1={MARGIN.left - 4} x2={MARGIN.left} y1={y} y2={y} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                <text x={MARGIN.left - 8} y={y} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatY(t)}
                </text>
              </g>
            );
          })}
          <text transform={`translate(${MARGIN.left - 48}, ${MARGIN.top + innerHeight / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
            Observed (mg/L)
          </text>

          {/* X-axis */}
          <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
          {xTicks.map((t, i) => {
            const x = xScale(t);
            return (
              <g key={`xt-${i}`}>
                <line x1={x} x2={x} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight + 4} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                <text x={x} y={MARGIN.top + innerHeight + 16} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatX(t)}
                </text>
              </g>
            );
          })}
          <text x={MARGIN.left + innerWidth / 2} y={MARGIN.top + innerHeight + 32} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
            Theoretical Quantile
          </text>

          {view.rectPreview && (
            <rect
              x={view.rectPreview.x0}
              y={view.rectPreview.y0}
              width={view.rectPreview.x1 - view.rectPreview.x0}
              height={view.rectPreview.y1 - view.rectPreview.y0}
              fill="rgba(16, 185, 129, 0.12)"
              stroke="var(--dv-accent-emerald, #10b981)"
              strokeWidth={1}
              strokeDasharray="3 3"
              pointerEvents="none"
            />
          )}

          <rect
            x={MARGIN.left}
            y={MARGIN.top}
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
        </svg>
      </div>

      {hover && (
        <ChartTooltip visible x={hover.clientX} y={hover.clientY} placement="top">
          <div style={{ minWidth: 180, fontSize: 11 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 999, background: hover.point.color }} />
              <span style={{ fontWeight: 600 }}>{hover.point.well}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 12, fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)' }}>
              <span style={{ color: 'var(--dv-tooltip-unit, rgba(255,255,255,0.55))' }}>Theoretical</span>
              <span>{hover.point.theoretical.toFixed(3)}</span>
              <span style={{ color: 'var(--dv-tooltip-unit, rgba(255,255,255,0.55))' }}>Observed</span>
              <span>{hover.point.observed.toFixed(3)} mg/L</span>
            </div>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}
